import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calendar, MapPin, Loader2 } from "lucide-react";

// Personal invitation RSVP page — the destination of the Accept / Maybe /
// Decline buttons in invitation emails. Accepting collects party details and
// converts the invite into a confirmed registration with QR entry passes.
export default function Rsvp() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const preselect = (params.get("answer") || "").toLowerCase();
  const base = import.meta.env.VITE_APP_API_BASE_URL || "";
  const apiRoot = base.replace(/\/scheduleRegistration\/?$/, "");

  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | choose | accept-form | done | error | notfound
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [partySize, setPartySize] = useState(1);
  const [attendees, setAttendees] = useState([{ name: "" }]);
  const [phone, setPhone] = useState("");
  const [custom, setCustom] = useState([]);

  const maxParty = info?.maxPartySize || 1;
  const fields = useMemo(() => info?.event?.registrationFields || [], [info]);

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Missing invitation link.");
      return;
    }
    axios
      .get(`${apiRoot}/event-invites/respond`, { params: { token } })
      .then((r) => {
        const data = r.data.data;
        setInfo(data);
        setCustom(
          (data?.event?.registrationFields || []).map((f) => ({ label: f.label, value: "" }))
        );
        if (data?.status === "accepted") {
          setState("done");
          setMessage(
            "You have already accepted — your entry passes were emailed to you. Hari Om 🙏"
          );
        } else if (preselect === "decline" || preselect === "maybe") {
          setState("choose");
        } else if (preselect === "accept") {
          setState("accept-form");
        } else {
          setState("choose");
        }
      })
      .catch((e) => {
        setState(e.response?.status === 404 ? "notfound" : "error");
        setMessage("We could not find this invitation.");
      });
  }, [token, apiRoot, preselect]);

  const respond = async (answer, extra = {}) => {
    try {
      setSubmitting(true);
      const r = await axios.post(`${apiRoot}/event-invites/respond`, {
        token,
        answer,
        ...extra,
      });
      setMessage(r.data?.message || "Thank you. Hari Om 🙏");
      setState("done");
    } catch (e) {
      setMessage(e.response?.data?.message || "Something went wrong. Please try again.");
      setState("error");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAccept = async (e) => {
    e.preventDefault();
    for (let i = 0; i < fields.length; i++) {
      if (fields[i].required && !(custom[i]?.value || "").trim()) {
        setMessage(`${fields[i].label} is required.`);
        return;
      }
    }
    setMessage("");
    const size = clampPartySize();
    await respond("accept", {
      partySize: size,
      phone,
      attendees: attendees.slice(0, size),
      custom,
    });
  };

  // Allow the field to be empty WHILE typing (delete → type a new number);
  // rows resize for valid digits and the value is clamped on blur/submit.
  const changePartySize = (raw) => {
    if (raw === "") {
      setPartySize("");
      return;
    }
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return;
    setPartySize(parsed);
    const next = Math.max(1, Math.min(maxParty, parsed));
    setAttendees((current) => Array.from({ length: next }, (_, i) => current[i] || { name: "" }));
  };

  const clampPartySize = () => {
    const parsed = parseInt(partySize || "1", 10);
    const next = isNaN(parsed) ? 1 : Math.max(1, Math.min(maxParty, parsed));
    setPartySize(next);
    setAttendees((current) => Array.from({ length: next }, (_, i) => current[i] || { name: "" }));
    return next;
  };

  const eventCard = info?.event && (
    <div className="mb-6 rounded-lg border border-[#ead9cd] bg-[#fffaf5] p-4 text-left">
      <p className="text-lg font-semibold text-[#14201f]">{info.event.name}</p>
      <div className="mt-2 space-y-1 text-sm text-[#52615f]">
        {info.event.date && (
          <p className="flex items-center gap-2">
            <Calendar size={15} className="text-[#9d1f1c]" />
            {new Date(info.event.date).toDateString()}
          </p>
        )}
        {info.event.location && (
          <p className="flex items-center gap-2">
            <MapPin size={15} className="text-[#9d1f1c]" />
            {info.event.location}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center font-serif text-[#241511]">
      <h1 className="text-2xl text-[#9d1f1c] mb-4">Your invitation</h1>
      {state === "loading" && <p>Please wait…</p>}
      {state === "notfound" && <p>{message}</p>}
      {(state === "error" || (message && state === "accept-form")) && (
        <p className="mb-4 text-red-700">{message}</p>
      )}

      {state === "done" && <p className="text-green-800">{message}</p>}

      {(state === "choose" || state === "accept-form") && info && (
        <>
          <p className="mb-4">
            Hari Om {info.name}, it would be our honour to welcome you
            {maxParty > 1 ? ` (up to ${maxParty} people)` : ""}.
          </p>
          {eventCard}
        </>
      )}

      {state === "choose" && info && (
        <div className="space-y-3">
          {preselect === "decline" && (
            <p className="text-sm text-[#52615f]">
              You chose “Unable to come” — please confirm below.
            </p>
          )}
          {preselect === "maybe" && (
            <p className="text-sm text-[#52615f]">You chose “Maybe” — please confirm below.</p>
          )}
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setState("accept-form")}
              disabled={submitting}
              className="rounded-lg bg-[#1c6b30] px-6 py-2.5 text-white hover:bg-[#155224]"
            >
              Accept with joy
            </button>
            <button
              onClick={() => respond("maybe")}
              disabled={submitting}
              className="rounded-lg border border-[#c9a24b] px-6 py-2.5 text-[#9a7b2e] hover:bg-[#fbf3e6]"
            >
              Maybe
            </button>
            <button
              onClick={() => respond("decline")}
              disabled={submitting}
              className="rounded-lg border border-[#5a0000] px-6 py-2.5 text-[#5a0000] hover:bg-[#fdf3ee]"
            >
              Unable to come
            </button>
          </div>
        </div>
      )}

      {state === "accept-form" && info && (
        <form onSubmit={submitAccept} className="space-y-4 text-left">
          {info.availableSeats < 1 && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              The event is currently full — accepting may not succeed. You can also reply Maybe.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of people <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={1}
                max={maxParty}
                value={partySize}
                onChange={(e) => changePartySize(e.target.value)}
                onBlur={clampPartySize}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
              />
              <p className="mt-1 text-xs text-gray-500">Invitation admits up to {maxParty}.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 000 0000"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
              />
            </div>
          </div>

          {attendees.slice(0, partySize).map((attendee, i) => (
            <div key={i}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attendee {i + 1} name <span className="text-red-500">*</span>
              </label>
              <input
                value={attendee.name}
                onChange={(e) =>
                  setAttendees((current) =>
                    current.map((a, j) => (j === i ? { name: e.target.value } : a))
                  )
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
              />
            </div>
          ))}

          {fields.map((field, i) => (
            <div key={`${field.label}-${i}`}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.type === "select" ? (
                <select
                  value={custom[i]?.value || ""}
                  onChange={(e) =>
                    setCustom((current) =>
                      current.map((c, j) => (j === i ? { ...c, value: e.target.value } : c))
                    )
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                >
                  <option value="">Select an option</option>
                  {(field.options || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={custom[i]?.value || ""}
                  maxLength={300}
                  onChange={(e) =>
                    setCustom((current) =>
                      current.map((c, j) => (j === i ? { ...c, value: e.target.value } : c))
                    )
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                />
              )}
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={() => setState("choose")}
              className="text-sm text-[#52615f] underline-offset-2 hover:underline"
            >
              ← Back
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-[#1c6b30] px-8 py-2.5 text-white hover:bg-[#155224] disabled:opacity-70 inline-flex items-center"
            >
              {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
              Confirm — send my QR passes
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
